<template lang="pug">
.breadcrumbs.app-breadcrumbs
	router-link(to="/") {{ projectName }}
	template(v-if="label")
		v-icon mdi-chevron-right
		router-link.screen-parent(v-if="labelTo" :to="labelTo") {{ label }}
		span.screen-label(v-else) {{ label }}
	template(v-if="showSite && displaySiteName")
		v-icon mdi-chevron-right
		v-menu(
			v-model="siteMenuOpen"
			offset-y
			bottom
			left
			:close-on-content-click="false"
			content-class="app-breadcrumbs-site-menu"
			@input="onSiteMenuToggle"
		)
			template(v-slot:activator="{ on, attrs }")
				span.site-crumb(v-bind="attrs" v-on="on" role="button" tabindex="0")
					span.site-name {{ displaySiteName }}
					v-icon.site-caret(small) mdi-menu-down
			v-card.app-breadcrumbs-site-card(flat)
				HaqAutocomplete(
					ref="siteAutocomplete"
					v-model="menuSiteId"
					:items="siteOptions"
					item-text="name"
					item-value="objectId"
					hide-details
					autofocus
					:menu-props="{ maxHeight: 320, bottom: true, offsetY: true }"
					@input="onMenuSiteChange"
				)
	slot
</template>

<script>
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';

/** Sentinel id for the project-level "All Sites" breadcrumb option. */
const ALL_SITES_ID = '__all__';

/**
 * App-wide breadcrumbs: Project › Screen › [Site ▾] [› extras].
 * Site dropdown lists sites the user can access (store getSites), filtered to the current project when possible.
 * Project-level screens pass allowAllSites so the dropdown includes All Sites (selected by default).
 */
export default {
	name: 'AppBreadcrumbs',
	components: {
		HaqAutocomplete
	},
	props: {
		/** Current screen label (e.g. Heat Map, Devices). */
		label: {
			type: String,
			default: ''
		},
		/** Optional route for the screen label (parent crumb, e.g. Dashboards list). */
		labelTo: {
			type: [String, Object],
			default: null
		},
		/** When false, omit the site crumb (screens with no site context). */
		showSite: {
			type: Boolean,
			default: true
		},
		/** When true, include All Sites and default to it when no site is selected. */
		allowAllSites: {
			type: Boolean,
			default: false
		},
		/** Optional site object override. */
		site: {
			type: Object,
			default: null
		},
		/** Optional site id when site object is not passed. */
		siteId: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			loadedSites: [],
			siteMenuOpen: false,
			menuSiteId: null
		};
	},
	computed: {
		/**
		 * Display name for the project root crumb.
		 * @returns {string}
		 */
		projectName() {
			const p = this.$store.state.project;
			return (p && (p.name || p.dis)) || 'Project';
		},
		/**
		 * Current project id from store or route.
		 * @returns {string|null}
		 */
		projectId() {
			return (this.$store.state.project && this.$store.state.project.objectId)
				|| this.$route.params.projectId
				|| null;
		},
		/**
		 * Resolved current site id. Project-level screens ignore leftover store site and route params.
		 * @returns {string|null}
		 */
		currentSiteId() {
			if (this.siteId) return this.siteId;
			if (this.site && this.site.objectId) return this.site.objectId;
			if (this.allowAllSites) {
				return this.$route.query.siteId || null;
			}
			const storeSite = this.$store.state.site;
			if (storeSite && storeSite.objectId) return storeSite.objectId;
			return this.$route.params.siteId || this.$route.query.siteId || null;
		},
		/**
		 * Site name for the site crumb, or All Sites on project-level screens.
		 * @returns {string}
		 */
		displaySiteName() {
			if (this.allowAllSites && !this.currentSiteId) return 'All Sites';
			if (this.site && this.site.name) return this.site.name;
			const storeSite = this.$store.state.site;
			if (storeSite && storeSite.objectId === this.currentSiteId && storeSite.name) {
				return storeSite.name;
			}
			const match = this.realSiteOptions.find(s => s.objectId === this.currentSiteId);
			return (match && match.name) || '';
		},
		/**
		 * Sites the user can access, scoped to the current project when project refs exist.
		 * @returns {Array}
		 */
		realSiteOptions() {
			const all = (this.loadedSites && this.loadedSites.length)
				? this.loadedSites
				: (this.$store.state.sites || []);
			const pid = this.projectId;
			if (!pid) return all.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
			const filtered = all.filter(s => {
				const sp = s.project;
				if (!sp) return true;
				const spId = sp.objectId || sp.id || sp;
				return spId === pid;
			});
			return filtered.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
		},
		/**
		 * Dropdown items, with All Sites first when the screen supports it.
		 * @returns {Array}
		 */
		siteOptions() {
			const sites = this.realSiteOptions;
			if (!this.allowAllSites) return sites;
			return [{ objectId: ALL_SITES_ID, name: 'All Sites' }].concat(sites);
		}
	},
	watch: {
		showSite: {
			immediate: true,
			/**
			 * Load accessible sites when the site crumb is shown.
			 * @param {boolean} val
			 */
			handler(val) {
				if (val) this.ensureSites();
			}
		}
	},
	methods: {
		/**
		 * Ensure Vuex sites are loaded for the dropdown.
		 */
		async ensureSites() {
			try {
				const sites = await this.$store.dispatch('getSites');
				this.loadedSites = sites || this.$store.state.sites || [];
			} catch (e) {
				console.error(e);
				this.loadedSites = this.$store.state.sites || [];
			}
		},
		/**
		 * Sync autocomplete value when the site menu opens; focus the field.
		 * @param {boolean} open
		 */
		onSiteMenuToggle(open) {
			if (open) {
				this.menuSiteId = this.currentSiteId || (this.allowAllSites ? ALL_SITES_ID : null);
				this.$nextTick(() => {
					const ac = this.$refs.siteAutocomplete;
					const input = ac && ac.$el && ac.$el.querySelector('input');
					if (input) input.focus();
					else if (ac && typeof ac.focus === 'function') ac.focus();
				});
			}
		},
		/**
		 * Handle site pick from the searchable autocomplete.
		 * @param {string} siteId
		 */
		onMenuSiteChange(siteId) {
			if (!siteId) return;
			if (siteId === ALL_SITES_ID) {
				this.siteMenuOpen = false;
				if (!this.currentSiteId) return;
				this.selectAllSites();
				return;
			}
			if (siteId === this.currentSiteId) {
				this.siteMenuOpen = false;
				return;
			}
			const nextSite = this.realSiteOptions.find(s => s.objectId === siteId);
			if (!nextSite) return;
			this.siteMenuOpen = false;
			this.selectSite(nextSite);
		},
		/**
		 * Navigate to the project-level version of the current screen (no site).
		 */
		selectAllSites() {
			const projectId = this.projectId;
			const path = this.$route.path || '';
			const query = { ...this.$route.query };
			delete query.siteId;
			this.$store.commit('setAppSiteId', null);
			/**
			 * Push a route; ignore duplicate-navigation errors.
			 * @param {object} loc
			 */
			const go = (loc) => {
				this.$router.push(loc).catch(function() { /* ignore */ });
			};
			if (path.includes('/pointexplorer') && projectId) {
				go({ path: `/projects/${projectId}/pointexplorer`, query });
				return;
			}
			if (path.includes('/virtualedge') && projectId) {
				go({ path: `/projects/${projectId}/virtualedge`, query });
				return;
			}
			if (path.includes('/devices') && projectId) {
				go({ path: `/projects/${projectId}/devices`, query });
				return;
			}
			if (path.includes('/dashboards')) {
				go({ path, query });
				return;
			}
			go({ path, query });
		},
		/**
		 * Navigate to the same screen for another site the user can access.
		 * @param {object} nextSite
		 */
		selectSite(nextSite) {
			if (!nextSite || !nextSite.objectId) return;
			if (nextSite.objectId === this.currentSiteId) return;
			const siteId = nextSite.objectId;
			this.$store.commit('setSite', nextSite);
			this.$store.commit('setAppSiteId', siteId);
			const projectId = this.projectId
				|| (nextSite.project && (nextSite.project.objectId || nextSite.project.id))
				|| this.$route.params.projectId;
			const path = this.$route.path || '';
			/**
			 * Push a route; ignore duplicate-navigation errors.
			 * @param {object} loc
			 */
			const go = (loc) => {
				this.$router.push(loc).catch(function() { /* ignore */ });
			};

			if (path.includes('/pointexplorer') || path.includes('/virtualedge') || path.includes('/dashboards') || path.includes('/devices')) {
				if (path === '/devices' || (path.startsWith('/devices') && !path.includes('/projects/'))) {
					if (projectId) {
						go({ path: `/projects/${projectId}/devices`, query: { ...this.$route.query, siteId } });
					} else {
						go({ path: '/devices', query: { ...this.$route.query, siteId } });
					}
					return;
				}
				go({ path, query: { ...this.$route.query, siteId } });
				return;
			}
			if (path === '/kiosks' || path.startsWith('/kiosks')) {
				go({ path: '/kiosks', query: { ...this.$route.query, siteId } });
				return;
			}
			if (this.$route.query.siteId != null && !this.$route.params.siteId) {
				go({
					path: this.$route.path,
					query: { ...this.$route.query, siteId }
				});
				return;
			}
			if (this.$route.params.siteId) {
				// Keep the same screen type; drop zone so we don't carry another site's zone.
				const nextPath = path
					.replace(/\/sites\/[^/]+/, `/sites/${siteId}`)
					.replace(/\/zones\/[^/]+.*/, '');
				go({ path: nextPath, query: this.$route.query });
				return;
			}
			if (projectId) {
				go({ path: `/projects/${projectId}/sites/${siteId}` });
			}
		}
	}
};
</script>

<style lang="scss">
.app-breadcrumbs {
	.site-crumb {
		display: inline-flex;
		align-items: center;
		cursor: pointer;
		color: var(--app-tertiary-txt);
		&:hover {
			text-decoration: underline;
		}
		.site-caret {
			margin: 0 0 0 2px !important;
			top: 0 !important;
		}
	}
	.screen-label {
		color: var(--app-primary-txt);
	}
	.screen-parent {
		color: var(--app-tertiary-txt);
		&:hover {
			text-decoration: underline;
		}
	}
}
.app-breadcrumbs-site-menu {
	.v-list {
		background: var(--app-tertiary-bg) !important;
		color: var(--app-primary-txt);
	}
}
.app-breadcrumbs-site-card {
	background: var(--app-tertiary-bg) !important;
	min-width: 280px;
	padding: 12px;
}
</style>
